import React, { forwardRef, HTMLAttributes, ReactNode } from 'react';
import { motion } from 'framer-motion';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../../utils/cn';

/**
 * Card variants using class-variance-authority for type-safe styling
 */
const cardVariants = cva(
  // Base styles
  `
    block
    bg-white dark:bg-neutral-900
    border border-neutral-200 dark:border-neutral-800
    transition-all duration-200 ease-smooth
    group
  `,
  {
    variants: {
      variant: {
        // Default - Standard card appearance
        default: `
          shadow-sm
          hover:shadow-md hover:border-neutral-300 dark:hover:border-neutral-700
        `,
        
        // Elevated - More prominent shadow
        elevated: `
          shadow-md
          hover:shadow-lg hover:border-neutral-300 dark:hover:border-neutral-700
        `,
        
        // Interactive - Clear hover states for clickable cards
        interactive: `
          shadow-sm cursor-pointer
          hover:shadow-lg hover:border-primary-300 dark:hover:border-primary-700
          hover:bg-neutral-50 dark:hover:bg-neutral-800
          active:scale-[0.99] active:shadow-sm
        `,
        
        // Outlined - Prominent border, minimal shadow
        outlined: `
          border-2 shadow-none
          hover:border-primary-300 dark:hover:border-primary-700
          hover:shadow-sm
        `,
        
        // Gradient - Premium feel with gradient border
        gradient: `
          relative overflow-hidden
          border-0 shadow-lg
          bg-gradient-to-r from-white to-neutral-50
          dark:from-neutral-900 dark:to-neutral-800
          hover:shadow-xl
          before:absolute before:inset-0 before:p-[1px] 
          before:bg-gradient-to-r before:from-primary-500 before:to-secondary-500
          before:rounded-[inherit] before:-z-10
          before:content-['']
        `,
        
        // Glass - Modern glassmorphism effect
        glass: `
          backdrop-blur-md bg-white/70 dark:bg-neutral-900/70
          border-white/20 dark:border-neutral-800/20
          shadow-xl
          hover:bg-white/80 dark:hover:bg-neutral-900/80
        `,
      },
      
      size: {
        sm: 'p-4 rounded-lg',
        md: 'p-6 rounded-xl',
        lg: 'p-8 rounded-2xl',
        xl: 'p-10 rounded-3xl',
      },
      
      spacing: {
        none: 'space-y-0',
        sm: 'space-y-2',
        md: 'space-y-4',
        lg: 'space-y-6',
        xl: 'space-y-8',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
      spacing: 'md',
    },
  }
);

/**
 * Card header variants
 */
const cardHeaderVariants = cva(
  'flex items-center justify-between',
  {
    variants: {
      spacing: {
        none: 'mb-0',
        sm: 'mb-2',
        md: 'mb-4',
        lg: 'mb-6',
        xl: 'mb-8',
      },
    },
    defaultVariants: {
      spacing: 'md',
    },
  }
);

/**
 * Card title variants
 */
const cardTitleVariants = cva(
  'font-semibold text-neutral-900 dark:text-neutral-100',
  {
    variants: {
      size: {
        sm: 'text-lg',
        md: 'text-xl',
        lg: 'text-2xl',
        xl: 'text-3xl',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
);

/**
 * Card description variants
 */
const cardDescriptionVariants = cva(
  'text-neutral-600 dark:text-neutral-400',
  {
    variants: {
      size: {
        sm: 'text-sm',
        md: 'text-base',
        lg: 'text-lg',
        xl: 'text-xl',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
);

/**
 * Card component props
 */
export interface CardProps
  extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {
  /** Content to render inside the card */
  children?: ReactNode;
  /** Whether the card is clickable */
  interactive?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Custom hover effects */
  hoverEffect?: 'none' | 'lift' | 'glow' | 'scale';
  /** Loading state */
  loading?: boolean;
}

/**
 * Card Component
 * A comprehensive card component with multiple variants and smooth animations
 */
export const Card = forwardRef<HTMLDivElement, CardProps>(
  (
    {
      children,
      variant = 'default',
      size = 'md',
      spacing = 'md',
      interactive = false,
      className,
      hoverEffect = 'none',
      loading = false,
      ...props
    },
    ref
  ) => {
    const isInteractive = interactive || variant === 'interactive';
    
    const getHoverAnimation = () => {
      if (loading) return {};
      
      switch (hoverEffect) {
        case 'lift':
          return { y: -4, transition: { duration: 0.2 } };
        case 'glow':
          return { 
            boxShadow: '0 0 20px rgba(59, 130, 246, 0.3)',
            transition: { duration: 0.2 }
          };
        case 'scale':
          return { scale: 1.02, transition: { duration: 0.2 } };
        default:
          return isInteractive ? { y: -2, transition: { duration: 0.2 } } : {};
      }
    };

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        whileHover={getHoverAnimation()}
        transition={{
          type: 'spring',
          stiffness: 400,
          damping: 30,
        }}
      >
        <div
          ref={ref}
          className={cn(
            cardVariants({ variant, size, spacing }),
            loading && 'animate-pulse cursor-wait',
            className
          )}
          {...props}
        >
          {/* Loading overlay */}
          {loading && (
            <motion.div
              className="absolute inset-0 bg-neutral-100/50 dark:bg-neutral-800/50 rounded-[inherit] flex items-center justify-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.div
                className="w-8 h-8 border-2 border-primary-600 border-t-transparent rounded-full"
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              />
            </motion.div>
          )}
          
          {/* Gradient border overlay for gradient variant */}
          {variant === 'gradient' && (
            <div className="absolute inset-0 bg-gradient-to-r from-white to-neutral-50 dark:from-neutral-900 dark:to-neutral-800 rounded-[inherit] z-10" />
          )}
          
          {/* Content */}
          <div className={variant === 'gradient' ? 'relative z-20' : 'relative'}>
            {children}
          </div>
        </div>
      </motion.div>
    );
  }
);

Card.displayName = 'Card';

/**
 * Card Header Component
 */
interface CardHeaderProps
  extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardHeaderVariants> {
  children?: ReactNode;
  className?: string;
}

export const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ children, spacing = 'md', className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(cardHeaderVariants({ spacing }), className)}
        {...props}
      >
        {children}
      </div>
    );
  }
);

CardHeader.displayName = 'CardHeader';

/**
 * Card Title Component
 */
interface CardTitleProps
  extends HTMLAttributes<HTMLHeadingElement>,
    VariantProps<typeof cardTitleVariants> {
  children?: ReactNode;
  className?: string;
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
}

export const CardTitle = forwardRef<HTMLHeadingElement, CardTitleProps>(
  ({ children, size = 'md', className, as: Component = 'h3', ...props }, ref) => {
    return (
      <Component
        ref={ref}
        className={cn(cardTitleVariants({ size }), className)}
        {...props}
      >
        {children}
      </Component>
    );
  }
);

CardTitle.displayName = 'CardTitle';

/**
 * Card Description Component
 */
interface CardDescriptionProps
  extends HTMLAttributes<HTMLParagraphElement>,
    VariantProps<typeof cardDescriptionVariants> {
  children?: ReactNode;
  className?: string;
}

export const CardDescription = forwardRef<HTMLParagraphElement, CardDescriptionProps>(
  ({ children, size = 'md', className, ...props }, ref) => {
    return (
      <p
        ref={ref}
        className={cn(cardDescriptionVariants({ size }), className)}
        {...props}
      >
        {children}
      </p>
    );
  }
);

CardDescription.displayName = 'CardDescription';

/**
 * Card Content Component
 */
interface CardContentProps extends HTMLAttributes<HTMLDivElement> {
  children?: ReactNode;
  className?: string;
}

export const CardContent = forwardRef<HTMLDivElement, CardContentProps>(
  ({ children, className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn('flex-1', className)}
        {...props}
      >
        {children}
      </div>
    );
  }
);

CardContent.displayName = 'CardContent';

/**
 * Card Footer Component
 */
interface CardFooterProps extends HTMLAttributes<HTMLDivElement> {
  children?: ReactNode;
  className?: string;
  spacing?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
}

export const CardFooter = forwardRef<HTMLDivElement, CardFooterProps>(
  ({ children, className, spacing = 'md', ...props }, ref) => {
    const spacingMap = {
      none: 'mt-0',
      sm: 'mt-2',
      md: 'mt-4',
      lg: 'mt-6',
      xl: 'mt-8',
    };

    return (
      <div
        ref={ref}
        className={cn(
          'flex items-center justify-between',
          spacingMap[spacing],
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);

CardFooter.displayName = 'CardFooter';

/**
 * Card Grid Component
 * Helper component for laying out multiple cards
 */
interface CardGridProps extends HTMLAttributes<HTMLDivElement> {
  children?: ReactNode;
  className?: string;
  columns?: 1 | 2 | 3 | 4 | 5 | 6;
  gap?: 'sm' | 'md' | 'lg' | 'xl';
}

export const CardGrid: React.FC<CardGridProps> = ({
  children,
  className,
  columns = 3,
  gap = 'md',
  ...props
}) => {
  const columnMap = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
    5: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5',
    6: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6',
  };

  const gapMap = {
    sm: 'gap-4',
    md: 'gap-6',
    lg: 'gap-8',
    xl: 'gap-10',
  };

  return (
    <div
      className={cn(
        'grid',
        columnMap[columns],
        gapMap[gap],
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
};

export default Card; 